import { FormControl, MenuItem, Select } from '@material-ui/core';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useProjectDetail } from 'lib/hooks/project/useProjectDetail';
import { useProjectDetailStore } from 'store/local/projectDetail';
import moment from 'moment';
import { T } from 'components/common/text';
import { genderList } from 'lib/mapper';
import MuiWrapper from 'components/common/input/MuiWrapper';
import WhiteButton from 'components/common/button/WhiteButton';
import CustomText from 'components/common/text/CustomText';
import { IRemake, IRework } from 'lib/types/projectTypes';
import { useProject } from 'lib/hooks/project/useProject';
import PlainModal from 'components/common/modal/PlainModal';
import { useShallowAppSelector } from 'store/hooks';

function ProjectDetailInformation() {
  const { t } = useTranslation();
  const { isProjectCreator, reworkList, prevPartnerManager } = useProjectDetailStore();
  const { language, projectInfo, hasSyncAlarm, partnerManager, managerList, remakeList } =
    useProject();
  const {
    projectId,
    dueDate,
    manager,
    partnerIdx,
    patientInfo,
    ownerRemakeType,
    partnerName,
    offlineYn,
  } = projectInfo;
  const {
    isCreatedOnlineProject,
    handleChangePartnerManager,
    handleOpenReworkStatusModal,
    handleEditInformation,
  } = useProjectDetail();

  return (
    <StyledProjectDetailInformation data-component-name="ProjectDetailInformation">
      <h2 className="sr-only">
        <T>PROJECT_INFORMATION</T>
      </h2>
      <div className="projectDetailInformation__container">
        <div className="projectDetailInformation__row projectId">
          <div className="projectDetailInformation__row_label">
            <span>
              <T>PROJECT_ID</T>
            </span>
          </div>
          <div className="projectDetailInformation__row_cotents projectId">
            <span className="projectDetailInformation__content projectId">{projectId}</span>
          </div>
        </div>
        <div className="projectDetailInformation__row client">
          <div className="projectDetailInformation__row_label">
            <span>
              <T>GLOBAL_PATIENT</T>
            </span>
          </div>
          <div className="projectDetailInformation__row_cotents client">
            <span className="projectDetailInformation__content client">
              {!!patientInfo
                ? `${patientInfo?.firstName} ${
                    !!patientInfo?.lastName ? patientInfo.lastName : ''
                  }${!!patientInfo?.identification ? patientInfo?.identification : ''}, ${
                    genderList[patientInfo?.gender as keyof typeof genderList]
                  }${moment(patientInfo?.birthday).isValid() ? `, ${patientInfo.birthday}` : ''}`
                : '-'}
            </span>
            {/* <div className="projectDetailInformation__content client">
            {!!patientInfo
              ? `${patientInfo?.firstName} ${!!patientInfo?.lastName ? patientInfo.lastName : ''}${
                  !!patientInfo?.identification ? patientInfo?.identification : ''
                }, ${genderList[patientInfo?.gender as keyof typeof genderList]}${
                  moment(patientInfo?.birthday).isValid() ? `, ${patientInfo.birthday}` : ''
                }`
              : '-'}
          </div> */}
          </div>
        </div>

        <div className="projectDetailInformation__row duedate">
          <div className="projectDetailInformation__row_label">
            <span>
              <T>PROJECT_DUE_DATE</T>
            </span>
          </div>
          <div className="projectDetailInformation__row_cotents duedate">
            <span className="projectDetailInformation__content duedate">
              {!dueDate
                ? '-'
                : language === 'en'
                ? moment.unix(dueDate).locale(language).format('MMM. DD. YYYY HH:mm')
                : moment.unix(dueDate).locale(language).format('YYYY. MM. DD  HH:mm')}
            </span>
          </div>
        </div>

        <div className="projectDetailInformation__row manager">
          <div className="projectDetailInformation__row_label">
            <span>
              <T>PROJECT_MANAGER</T>
            </span>
          </div>
          {isProjectCreator || !partnerIdx ? (
            <div className="projectDetailInformation__row_cotents manager">
              <span className="projectDetailInformation__content manager">
                {isProjectCreator ? manager : '-'}
              </span>
            </div>
          ) : (
            <>
              <div className="projectDetailInformation__row_cotents selectManager">
                <span className="projectDetailInformation__content selectManager">
                  {!hasSyncAlarm && (
                    <MuiWrapper className="xs partner_select_wrapper">
                      <FormControl variant="outlined" className="partner_select_box">
                        <Select
                          className="partner_select"
                          MenuProps={{
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                          }}
                          disabled={!partnerIdx}
                          name="partnerManager"
                          displayEmpty
                          value={partnerManager.value}
                          onChange={partnerManager.onChange}
                        >
                          {managerList.length > 0 &&
                            managerList.map((item: any, idx: number) => {
                              return (
                                <MenuItem value={`${item.employeeIdx}`} key={idx}>
                                  {item.userName}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                    </MuiWrapper>
                  )}
                </span>
              </div>
              <div className="projectDetailInformation__content_button">
                <WhiteButton
                  width={100}
                  height={34}
                  disabled={!partnerIdx || Number(partnerManager.value) === prevPartnerManager}
                  borderRadius={50}
                  onClick={() => handleChangePartnerManager(Number(partnerManager.value))}
                >
                  <CustomText fontSize={14} fontWeight={400}>
                    <T>GLOBAL_SAVE</T>
                  </CustomText>
                </WhiteButton>
              </div>
            </>
          )}
        </div>
        <div className="projectDetailInformation__row partner">
          <div className="projectDetailInformation__row_label">
            <span>
              <T>GLOBAL_PARTNER</T>
            </span>
          </div>
          <div className="projectDetailInformation__row_cotents partner">
            <span className="projectDetailInformation__content partner">
              {!!partnerName ? partnerName : '-'}
            </span>
          </div>
        </div>
      </div>
      <div className="projectDetailInformation__button_box">
        <div className="projectDetailInformation__content_button">
          <WhiteButton
            width={100}
            height={34}
            borderRadius={50}
            disabled={isCreatedOnlineProject}
            onClick={() => handleEditInformation()}
          >
            <CustomText fontSize={14} fontWeight={400}>
              <T>GLOBAL_EDIT</T>
            </CustomText>
          </WhiteButton>
        </div>
      </div>
    </StyledProjectDetailInformation>
  );
}

const StyledProjectDetailInformation = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  .projectDetailInformation__container {
    padding: 10px;
    border: 1px solid #e1e1e7;
    border-radius: 3px;
    .projectDetailInformation__row {
      width: 100%;
      display: flex;
      .projectDetailInformation__row_label {
        width: 150px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        font-size: 14px;
        font-weight: 600;
        min-height: 38px;
        line-height: 17px;
        &.remake {
          align-items: flex-start;
          margin-top: 10.5px;
          min-height: 27px;
          /* padding: 10.5px 0; */
        }
      }
      .projectDetailInformation__row_cotents {
        width: calc(100% - 150px);
        word-break: break-all;
        white-space: break-spaces;
        min-height: 38px;
        display: flex;
        align-items: center;

        &.remake {
          overflow-y: scroll;
          margin-top: 10.5px;
          min-height: 27px;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
        }
        &.selectManager {
          padding: 0;
          .partner_select {
            width: 200px;
          }
        }
        &.selectManager,
        &.rework {
          width: calc(100% - 250px);
          column-gap: 10px;
        }

        .projectDetailInformation__content {
          font-size: 13px;
          font-weight: 400;
          line-height: 17px;
          display: flex;
          row-gap: 10px;
          max-height: 100px;
          &:not(.rework) {
            flex-direction: column;
          }
        }
      }
    }
  }
  .projectDetailInformation__button_box {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    .projectDetailInformation__content_button {
      height: 38px;
      display: flex;
      align-items: center;
    }
  }
`;

export default ProjectDetailInformation;
